// Client HTTP de l'API Bailivo : cookies de session, jeton CSRF recopié, erreurs en français.

export type FieldError = { field: string; message: string };

export class ApiError extends Error {
  status: number;
  code: string;
  fields: FieldError[];
  constructor(status: number, code: string, message: string, fields: FieldError[] = []) {
    super(message);
    this.status = status;
    this.code = code;
    this.fields = fields;
  }
  fieldMessage(field: string): string | undefined {
    return this.fields.find((f) => f.field === field)?.message;
  }
}

function csrfToken(): string {
  const m = document.cookie.match(/(?:^|;\s*)bailivo_csrf=([^;]+)/);
  return m ? decodeURIComponent(m[1]) : "";
}

let csrfReady: Promise<void> | null = null;
async function ensureCsrf(): Promise<void> {
  if (csrfToken()) return;
  if (!csrfReady) csrfReady = fetch("/api/v1/health", { credentials: "same-origin" }).then(() => undefined);
  await csrfReady;
}

type Options = { method?: string; body?: unknown; form?: FormData; signal?: AbortSignal };

export async function api<T = any>(path: string, opts: Options = {}): Promise<T> {
  const method = opts.method ?? (opts.body !== undefined || opts.form ? "POST" : "GET");
  const headers: Record<string, string> = { Accept: "application/json" };
  if (method !== "GET") {
    await ensureCsrf();
    headers["x-csrf-token"] = csrfToken();
  }
  let body: BodyInit | undefined;
  if (opts.form) body = opts.form;
  else if (opts.body !== undefined) {
    headers["Content-Type"] = "application/json";
    body = JSON.stringify(opts.body);
  }
  let res: Response;
  try {
    res = await fetch(path, { method, headers, body, credentials: "same-origin", signal: opts.signal });
  } catch {
    throw new ApiError(0, "reseau", "Connexion au serveur impossible. Vérifiez votre réseau puis réessayez.");
  }
  const text = await res.text();
  let data: any = null;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = text;
    }
  }
  if (!res.ok) {
    const e = data?.error ?? {};
    throw new ApiError(res.status, e.code ?? "erreur", e.message ?? `Erreur ${res.status}`, e.fields ?? []);
  }
  return data as T;
}

export const get = <T = any>(path: string) => api<T>(path);
export const post = <T = any>(path: string, body: unknown = {}) => api<T>(path, { method: "POST", body });
export const put = <T = any>(path: string, body: unknown = {}) => api<T>(path, { method: "PUT", body });
export const patch = <T = any>(path: string, body: unknown = {}) => api<T>(path, { method: "PATCH", body });
export const del = <T = any>(path: string) => api<T>(path, { method: "DELETE" });
export const upload = <T = any>(path: string, form: FormData) => api<T>(path, { method: "POST", form });

/** Téléchargement d'un fichier protégé : ouvre une URL relative dans un nouvel onglet. */
export function fileUrl(path: string): string {
  return path;
}
